import { Controller } from "@hotwired/stimulus"

// Universal form validation controller for consistent UX across all forms
export default class extends Controller {
  static targets = [
    "submit",
    "errorContainer",
    "field"
  ]

  static values = {
    showLoadingOnSubmit: { type: Boolean, default: true },
    clearErrorsOnInput: { type: Boolean, default: true },
    validateOnInput: { type: Boolean, default: false }
  }

  connect() {
    // Store bound references for proper cleanup
    this._boundHandleSubmitStart = this.handleSubmitStart.bind(this)
    this._boundHandleSubmitEnd = this.handleSubmitEnd.bind(this)
    this._boundHandleSubmit = this.handleSubmit.bind(this)
    this._boundClearFieldErrors = this.clearFieldErrors.bind(this)

    // Bind form submission events
    this.element.addEventListener("turbo:submit-start", this._boundHandleSubmitStart)
    this.element.addEventListener("turbo:submit-end", this._boundHandleSubmitEnd)
    this.element.addEventListener("submit", this._boundHandleSubmit)

    // Bind input events for real-time validation
    if (this.clearErrorsOnInputValue) {
      this.fieldTargets.forEach(field => {
        field.addEventListener("input", this._boundClearFieldErrors)
        field.addEventListener("change", this._boundClearFieldErrors)
      })
    }

    // Initialize submission button state
    this.initializeSubmitButton()
  }

  disconnect() {
    this.element.removeEventListener("turbo:submit-start", this._boundHandleSubmitStart)
    this.element.removeEventListener("turbo:submit-end", this._boundHandleSubmitEnd)
    this.element.removeEventListener("submit", this._boundHandleSubmit)

    this.fieldTargets.forEach(field => {
      field.removeEventListener("input", this._boundClearFieldErrors)
      field.removeEventListener("change", this._boundClearFieldErrors)
    })
  }

  // Form submission handlers
  handleSubmitStart(event) {
    if (this.showLoadingOnSubmitValue) {
      this.showLoadingState()
    }
    this.clearFormErrors()
  }

  handleSubmitEnd(event) {
    this.hideLoadingState()

    if (event.detail.success) {
      this.handleSuccessfulSubmission(event)
    } else {
      this.handleFailedSubmission(event)
    }
  }

  handleSubmit(event) {
    // Client-side validation before submission
    if (!this.validateForm()) {
      event.preventDefault()
      return false
    }
  }

  // Loading state management
  showLoadingState() {
    this.submitTargets.forEach(button => {
      button.disabled = true
      button.setAttribute('data-original-text', button.textContent)

      const loadingText = button.dataset.loadingText || 'Saving...'
      const loadingIcon = '<span class="loading loading-spinner loading-xs mr-2"></span>'

      button.innerHTML = `${loadingIcon}${loadingText}`
      button.classList.add('loading-state')
    })

    // Add form-level loading class
    this.element.classList.add('form-loading')
  }

  hideLoadingState() {
    this.submitTargets.forEach(button => {
      button.disabled = false
      const originalText = button.getAttribute('data-original-text')
      if (originalText) {
        button.textContent = originalText
        button.removeAttribute('data-original-text')
      }
      button.classList.remove('loading-state')
    })

    this.element.classList.remove('form-loading')
  }

  // Error handling
  showError(message, field = null) {
    if (field) {
      this.showFieldError(field, message)
    } else {
      this.showFormError(message)
    }
  }

  showFormError(message) {
    if (this.hasErrorContainerTarget) {
      this.errorContainerTarget.innerHTML = `
        <div class="alert alert-error mb-4" role="alert">
          <div class="flex items-center">
            <svg class="w-5 h-5 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
            </svg>
            <span>${message}</span>
          </div>
        </div>
      `
      this.errorContainerTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }

  showFieldError(field, message) {
    // Add error styling to field
    field.classList.add('input-error', 'border-error')
    field.setAttribute('aria-invalid', 'true')

    // Find or create error message container
    let errorElement = field.parentNode.querySelector('.field-error')
    if (!errorElement) {
      errorElement = document.createElement('div')
      errorElement.className = 'field-error text-error text-sm mt-1'
      field.parentNode.appendChild(errorElement)
    }

    errorElement.textContent = message
    errorElement.style.display = 'block'
  }

  clearFormErrors() {
    if (this.hasErrorContainerTarget) {
      this.errorContainerTarget.innerHTML = ''
    }
  }

  clearFieldErrors(event) {
    const field = event.target
    this.clearFieldError(field)
  }

  clearFieldError(field) {
    field.classList.remove('input-error', 'border-error', 'textarea-error')
    field.setAttribute('aria-invalid', 'false')

    const errorElement = field.parentNode.querySelector('.field-error')
    if (errorElement) {
      errorElement.style.display = 'none'
    }
  }

  // Success handling
  handleSuccessfulSubmission(event) {
    // Clear any existing errors
    this.clearFormErrors()
    this.fieldTargets.forEach(field => this.clearFieldError(field))

    // Show success feedback if configured
    const successMessage = this.element.dataset.successMessage
    if (successMessage) {
      this.showSuccessMessage(successMessage)
    }

    // Reset form if configured
    if (this.element.dataset.resetOnSuccess === 'true') {
      this.resetForm()
    }
  }

  handleFailedSubmission(event) {
    // Handle validation errors from server response
    if (event.detail.xhr) {
      const response = event.detail.xhr.response
      try {
        const errors = this.extractErrorsFromResponse(response)
        this.displayErrors(errors)
      } catch (e) {
        this.showFormError('An error occurred. Please try again.')
      }
    }
  }

  // Validation
  validateForm() {
    let isValid = true

    this.fieldTargets.forEach(field => {
      if (!this.validateField(field)) {
        isValid = false
      }
    })

    return isValid
  }

  validateField(field) {
    // Basic HTML5 validation (skip elements without checkValidity, e.g. Trix editors)
    if (typeof field.checkValidity === 'function' && !field.checkValidity()) {
      this.showFieldError(field, field.validationMessage)
      return false
    }

    // Custom validation based on data attributes
    const customValidation = field.dataset.customValidation
    if (customValidation) {
      return this.runCustomValidation(field, customValidation)
    }

    return true
  }

  runCustomValidation(field, validationType) {
    switch (validationType) {
      case 'progress':
        return this.validateProgress(field)
      case 'email':
        return this.validateEmail(field)
      case 'required-if-changed':
        return this.validateRequiredIfChanged(field)
      default:
        return true
    }
  }

  validateProgress(field) {
    const value = parseInt(field.value)
    if (isNaN(value) || value < 0 || value > 100) {
      this.showFieldError(field, 'Progress must be between 0 and 100')
      return false
    }
    return true
  }

  validateEmail(field) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (field.value && !emailRegex.test(field.value)) {
      this.showFieldError(field, 'Please enter a valid email address')
      return false
    }
    return true
  }

  validateRequiredIfChanged(field) {
    const originalValue = field.dataset.originalValue || field.defaultValue
    const hasChanged = field.value !== originalValue

    if (hasChanged && !field.value.trim()) {
      this.showFieldError(field, 'This field is required when making changes')
      return false
    }
    return true
  }

  // Utility methods
  showSuccessMessage(message) {
    // Create temporary success notification
    const successElement = document.createElement('div')
    successElement.className = 'alert alert-success fixed top-4 right-4 z-50 shadow-lg'
    successElement.innerHTML = `
      <div class="flex items-center">
        <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
        </svg>
        <span>${message}</span>
      </div>
    `

    document.body.appendChild(successElement)

    // Remove after 3 seconds
    setTimeout(() => {
      successElement.remove()
    }, 3000)
  }

  resetForm() {
    this.element.reset()
    this.clearFormErrors()
    this.fieldTargets.forEach(field => this.clearFieldError(field))
  }

  initializeSubmitButton() {
    // Ensure submit buttons are properly configured
    this.submitTargets.forEach(button => {
      if (!button.dataset.loadingText) {
        button.dataset.loadingText = 'Saving...'
      }
    })
  }

  extractErrorsFromResponse(response) {
    // Try to extract errors from various response formats
    if (response.includes('alert-error') || response.includes('field-error')) {
      // Response contains rendered error HTML
      return { html: response }
    }

    // Could extend to handle JSON error responses
    return { message: 'Please check the form and try again.' }
  }

  displayErrors(errors) {
    if (errors.html) {
      // Server returned rendered error HTML - let Turbo handle it
      return
    }

    if (errors.message) {
      this.showFormError(errors.message)
    }

    if (errors.fields) {
      Object.entries(errors.fields).forEach(([fieldName, message]) => {
        const field = this.element.querySelector(`[name*="${fieldName}"]`)
        if (field) {
          this.showFieldError(field, message)
        }
      })
    }
  }
} 